export type EntitlementStatus = "unavailable" | "pending" | "active" | "refunded" | "charged_back" | "expired";
export type CommerceChannel = "web" | "ait" | "ios" | "android";
export type CommerceProviderId = "web_pg" | "apps_in_toss" | "app_store" | "google_play";
export type CommerceCapability = "advanced_queue" | "unlimited_presets" | "themes";
export type CoreCapability =
  | "meetup_feed" | "meetup_create" | "meetup_join" | "temporary_chat" | "meetup_safety" | "report_appeal"
  | "all_catalog" | "all_safety" | "pass" | "local_room" | "shared_room" | "recap" | "favorites"
  | "basic_preset" | "data_export_delete" | "accessibility";

export type ProviderReceipt = {
  provider: CommerceProviderId;
  orderHash: string;
  sku: keyof typeof commerceOffers;
  status: Exclude<EntitlementStatus, "unavailable">;
  updatedAt: string;
};

export const coreCapabilities: readonly CoreCapability[] = [
  "meetup_feed", "meetup_create", "meetup_join", "temporary_chat", "meetup_safety", "report_appeal",
  "all_catalog", "all_safety", "pass", "local_room", "shared_room", "recap", "favorites",
  "basic_preset", "data_export_delete", "accessibility",
];
export const paidCapabilities: readonly CommerceCapability[] = ["advanced_queue", "unlimited_presets", "themes"];

export const commerceOffers = {
  host_lifetime_candidate: {
    type: "non_consumable",
    grossPriceKrw: 7_900,
    capabilities: ["advanced_queue", "unlimited_presets", "themes"] as const,
  },
  theme_candidate: {
    type: "non_consumable",
    grossPriceKrw: 2_900,
    capabilities: ["themes"] as const,
  },
} as const;

export const commerceRuntimePolicy = {
  productionActivated: false,
  adsActivated: false,
  cardRequiredForTrial: false,
  existingFreeRightsMayBeRevoked: false,
  firstValueEvents: ["meetup_created", "meetup_joined"] as const,
  paywallAfterEvents: ["meetup_checked_in", "meetup_ended"] as const,
  receiptAuthority: "server_verified_provider_receipt",
} as const;

export const commerceProviderForChannel: Record<CommerceChannel, CommerceProviderId> = {
  web: "web_pg",
  ait: "apps_in_toss",
  ios: "app_store",
  android: "google_play",
};

export function canPresentCommerce(input: {
  meetupValueCompleted: boolean;
  providerReady: boolean;
  killSwitchActive?: boolean;
  entitlementStatus: EntitlementStatus;
  productionActivated?: boolean;
}) {
  return Boolean(
    input.meetupValueCompleted &&
    input.providerReady &&
    !input.killSwitchActive &&
    (input.productionActivated ?? commerceRuntimePolicy.productionActivated) &&
    input.entitlementStatus !== "active",
  );
}

export function resolveCapabilities(status: EntitlementStatus) {
  return { core: coreCapabilities, paid: status === "active" ? paidCapabilities : [] } as const;
}

const transitions: Record<EntitlementStatus, readonly EntitlementStatus[]> = {
  unavailable: ["pending"], pending: ["active", "refunded", "charged_back", "expired"],
  active: ["refunded", "charged_back", "expired"], refunded: ["pending"], charged_back: ["pending"], expired: ["pending"],
};

export function transitionEntitlement(current: EntitlementStatus, next: EntitlementStatus) {
  return current === next || transitions[current].includes(next) ? next : null;
}

export interface CommerceProvider {
  readonly id: CommerceProviderId;
  verify(orderReference: string): Promise<ProviderReceipt>;
  restore(subjectReference: string): Promise<ProviderReceipt[]>;
}

export async function verifyPurchase(input: { provider?: CommerceProvider; orderReference: string; existingOrderHashes: ReadonlySet<string> }) {
  if (!input.provider) return { status: "unavailable" as const };
  const receipt = await input.provider.verify(input.orderReference);
  return input.existingOrderHashes.has(receipt.orderHash) ? { status: "duplicate" as const, receipt } : { status: receipt.status, receipt };
}

export async function restorePurchases(input: { provider?: CommerceProvider; subjectReference: string; existingOrderHashes: ReadonlySet<string> }) {
  if (!input.provider) return { status: "unavailable" as const, receipts: [] as ProviderReceipt[] };
  const receipts = await input.provider.restore(input.subjectReference);
  return { status: "verified" as const, receipts: receipts.filter((receipt) => !input.existingOrderHashes.has(receipt.orderHash)) };
}

export type SettlementEntry = { occurredAt: string; kind: "order" | "refund" | "chargeback" | "fee" | "tax" | "provider_balance"; amountKrw: number };
export function previousKstDayWindow(now: string) {
  const shifted = new Date(Date.parse(now) + 9 * 60 * 60 * 1_000);
  const endShifted = Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate());
  return { from: new Date(endShifted - 9 * 60 * 60 * 1_000 - 24 * 60 * 60 * 1_000).toISOString(), to: new Date(endShifted - 9 * 60 * 60 * 1_000).toISOString() };
}

export function reconcilePreviousKstDay(entries: SettlementEntry[], now: string) {
  const window = previousKstDayWindow(now);
  const rows = entries.filter((entry) => entry.occurredAt >= window.from && entry.occurredAt < window.to);
  const sum = (kind: SettlementEntry["kind"]) => rows.filter((entry) => entry.kind === kind).reduce((total, entry) => total + entry.amountKrw, 0);
  const orders = sum("order"), refunds = sum("refund"), chargebacks = sum("chargeback"), fees = sum("fee"), taxes = sum("tax");
  const expectedNet = orders - refunds - chargebacks - fees - taxes;
  const providerBalance = sum("provider_balance");
  return { window, orders, refunds, chargebacks, fees, taxes, expectedNet, providerBalance, matched: expectedNet === providerBalance, killSwitchRequired: expectedNet !== providerBalance };
}

export function decideSessionEndAd() {
  return { show: false, reward: false, reason: "ads_not_selected" as const };
}
